// ===== components/ProviderProfile/FBInstaConnections/OAuthSuccessConfirmation.jsx - NEW =====

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Chip,
  Stack,
  CircularProgress,
  Avatar,
  Grid,
  Card,
  CardContent,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Business as BusinessIcon,
  ArrowForward as ArrowIcon,
  Celebration as CelebrationIcon,
  Security as SecurityIcon,
  Visibility as ViewIcon,
  Group as FollowersIcon,
  Article as PostIcon,
  Description as ContentIcon,
  ManageAccounts as ManageAccountsIcon
} from '@mui/icons-material';
import { useGetSocialAccountsQuery } from '../../../redux/thunks/ai/getsocialaccounts';

const OAuthSuccessConfirmation = ({ 
  open,
  connectionResult,
  onContinue,
  onViewAccounts,
  platform
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [loading, setLoading] = useState(true);
  const { data: socialAccountsData, refetch } = useGetSocialAccountsQuery();
  
  useEffect(() => {
    if (open) {
      // Refetch accounts to get the latest data
      refetch().then(() => {
        setLoading(false);
      });
    }
  }, [open, refetch]);

  // Permission icons mapping
  const permissionIcons = {
    'pages_show_list': <ViewIcon />,
    'pages_read_engagement': <FollowersIcon />,
    'pages_manage_posts': <PostIcon />,
    'business_management': <BusinessIcon />,
    'instagram_basic': <InstagramIcon />,
    'instagram_content_publish': <ContentIcon />
  };

  // Permission display names
  const permissionNames = {
    'pages_show_list': 'View Your Pages',
    'pages_read_engagement': 'Read Page Content & Engagement',
    'pages_manage_posts': 'Create & Manage Page Posts',
    'business_management': 'Access Business Manager',
    'instagram_basic': 'Access Instagram Profile Info',
    'instagram_content_publish': 'Post to Instagram'
  };

  if (!connectionResult) return null;

  // Get connected accounts summary
  const getAccountsSummary = () => {
    if (!socialAccountsData?.accounts) {
      return {
        facebookPages: [],
        instagramAccounts: [],
        totalAccounts: 0
      };
    }

    // Get recently connected accounts (within last 5 minutes)
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    const recentAccounts = socialAccountsData.accounts.filter(acc => {
      const createdAt = new Date(acc.createdAt);
      return createdAt >= fiveMinutesAgo;
    });

    const facebookPages = recentAccounts.filter(acc => acc.platform === 'facebook');
    const instagramAccounts = recentAccounts.filter(acc => acc.platform === 'instagram');

    return {
      facebookPages,
      instagramAccounts,
      totalAccounts: recentAccounts.length
    };
  };

  const accountsSummary = getAccountsSummary();
  const totalAccounts = accountsSummary.totalAccounts;

  // Get permissions from session storage or use default
  const acceptedPermissions = JSON.parse(
    sessionStorage.getItem('oauth_accepted_permissions') || '[]'
  );
  
  const permissions = acceptedPermissions.length > 0 ? acceptedPermissions : [
    'pages_show_list',
    'pages_read_engagement',
    'pages_manage_posts',
    'business_management',
    'instagram_basic',
    'instagram_content_publish'
  ];

  if (loading) {
    return (
      <Dialog 
        open={open} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
            <CircularProgress size={60} />
            <Typography variant="h6">
              Loading Account Details...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // ===== Google-specific render =====
  if (platform === 'google' || connectionResult?.platform === 'google') {
    return (
      <Dialog open={open} maxWidth="sm" fullWidth>
        <DialogContent sx={{ p: 4, textAlign: 'center' }}>
          
          <CelebrationIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          
          <Typography variant="h5" fontWeight={600} gutterBottom>
            🎉 Google Connected!
          </Typography>

          <Alert
            severity={connectionResult?.gbpPending ? 'info' : 'success'}
            sx={{ mb: 3, textAlign: 'left' }}
          >
            <Typography variant="body2">
              {connectionResult?.gbpPending
                ? 'Google account connected successfully. Business Profile review syncing will activate once API access is granted by Google.'
                : `Google Business Profile "${connectionResult?.accountName || 'your location'}" connected successfully.`
              }
            </Typography>
          </Alert>

          <Paper variant="outlined" sx={{ p: 2, mb: 3, textAlign: 'left' }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              What happens next:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              <li>
                <Typography variant="body2">
                  Google reviews will sync automatically to Revfer
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  AI will generate draft responses to new reviews
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  You review and approve responses before they post to Google
                </Typography>
              </li>
              {connectionResult?.gbpPending && (
                <li>
                  <Typography variant="body2" color="info.main">
                    <strong>Pending:</strong> Waiting for Google Business Profile API approval
                  </Typography>
                </li>
              )}
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{ p: 1.5, bgcolor: 'grey.50', mb: 3, textAlign: 'left' }}
          >
            <Typography variant="caption" color="text.secondary">
              <strong>Connected account:</strong> {connectionResult?.accountName || 'pankaj.aggarwal@revfer.com'}
            </Typography>
            <br />
            <Typography variant="caption" color="text.secondary">
              <strong>Connected:</strong> {new Date().toLocaleString('en-US', {
                weekday: 'long', year: 'numeric', month: 'long',
                day: 'numeric', hour: '2-digit', minute: '2-digit'
              })}
            </Typography>
          </Paper>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button
              variant="outlined"
              onClick={onViewAccounts}
              startIcon={<ManageAccountsIcon />}
              fullWidth
              size="large"
            >
              Manage Accounts
            </Button>
            <Button
              variant="contained"
              onClick={onContinue}
              endIcon={<ArrowIcon />}
              fullWidth
              size="large"
            >
              Done
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    );
  }



  return (
    <Dialog 
      open={open} 
      maxWidth="md" 
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          ...(isMobile && {
            margin: 0,
            maxHeight: '100%',
            borderRadius: 0
          })
        }
      }}
    >
      <DialogContent sx={{ p: isMobile ? 2 : 4 }}>
        {/* Success Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <CelebrationIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant={isMobile ? "h5" : "h4"} gutterBottom fontWeight="600">
            🎉 Connection Successful!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You have successfully connected {totalAccounts} account{totalAccounts !== 1 ? 's' : ''} to Revfer
          </Typography>
        </Box>

        {/* Quick Stats */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={4}>
            <Card elevation={0} sx={{ bgcolor: 'primary.lighter', textAlign: 'center' }}>
              <CardContent sx={{ py: isMobile ? 1.5 : 2 }}>
                <Typography variant={isMobile ? "h5" : "h4"} color="primary.main" fontWeight="700">
                  {permissions.length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Permissions
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4}>
            <Card elevation={0} sx={{ bgcolor: 'success.lighter', textAlign: 'center' }}>
              <CardContent sx={{ py: isMobile ? 1.5 : 2 }}>
                <Typography variant={isMobile ? "h5" : "h4"} color="success.main" fontWeight="700">
                  {accountsSummary.facebookPages.length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  FB Pages
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4}>
            <Card elevation={0} sx={{ bgcolor: '#FFF0F5', textAlign: 'center' }}>
              <CardContent sx={{ py: isMobile ? 1.5 : 2 }}>
                <Typography variant={isMobile ? "h5" : "h4"} sx={{ color: '#E4405F' }} fontWeight="700">
                  {accountsSummary.instagramAccounts.length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Instagram
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 4 }}>
          <Typography variant="body2">
            All accounts have been successfully connected and verified. You can now start creating 
            and posting AI-generated content to these accounts.
          </Typography>
        </Alert>

        {/* PERMISSIONS GRANTED SECTION */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            mb: 2,
            p: 1.5,
            bgcolor: 'success.lighter',
            borderRadius: 1
          }}>
            <SecurityIcon color="success" />
            <Typography variant={isMobile ? "body1" : "h6"} fontWeight="600">
              ✓ Permissions Granted ({permissions.length})
            </Typography>
          </Box>
          
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Grid container spacing={2}>
              {permissions.map((permission, idx) => (
                <Grid item xs={12} sm={6} key={idx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ 
                      p: 1, 
                      bgcolor: 'success.lighter', 
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      {permissionIcons[permission] || <CheckIcon />}
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight="600">
                        {permissionNames[permission] || permission}
                      </Typography>
                      <Chip 
                        label="Active" 
                        size="small" 
                        color="success"
                        sx={{ height: 18, fontSize: '0.7rem', mt: 0.5 }}
                      />
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* FACEBOOK PAGES CONNECTED */}
        {accountsSummary.facebookPages.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              mb: 2,
              p: 1.5,
              bgcolor: 'primary.lighter',
              borderRadius: 1
            }}>
              <FacebookIcon sx={{ color: '#1877F2' }} />
              <Typography variant={isMobile ? "body1" : "h6"} fontWeight="600">
                Facebook Pages Connected ({accountsSummary.facebookPages.length})
              </Typography>
            </Box>
            
            <Stack spacing={2}>
              {accountsSummary.facebookPages.map((page, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar 
                      src={page.profilePicture} 
                      sx={{ width: 56, height: 56 }}
                    >
                      {page.accountName?.charAt(0)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight="600">
                        {page.accountName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        @{page.username}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Chip 
                        label="Connected" 
                        color="success" 
                        size="small"
                        icon={<CheckIcon />}
                        sx={{ mb: 0.5 }}
                      />
                      <Typography variant="caption" display="block" color="text.secondary">
                        Ready to post
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}

        {/* INSTAGRAM ACCOUNTS CONNECTED */}
        {accountsSummary.instagramAccounts.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              mb: 2,
              p: 1.5,
              bgcolor: '#FFF0F5',
              borderRadius: 1
            }}>
              <InstagramIcon sx={{ color: '#E4405F' }} />
              <Typography variant={isMobile ? "body1" : "h6"} fontWeight="600">
                Instagram Business Accounts Connected ({accountsSummary.instagramAccounts.length})
              </Typography>
            </Box>
            
            <Stack spacing={2}>
              {accountsSummary.instagramAccounts.map((account, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar 
                      src={account.profilePicture} 
                      sx={{ width: 56, height: 56 }}
                    >
                      {account.accountName?.charAt(0)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight="600">
                        {account.accountName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        @{account.username}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Chip 
                        label="Connected" 
                        color="success" 
                        size="small"
                        icon={<CheckIcon />}
                        sx={{ mb: 0.5 }}
                      />
                      <Typography variant="caption" display="block" color="text.secondary">
                        Ready to post
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* IMPORTANT INFORMATION */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="600" gutterBottom>
            What This Means:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            <li>
              <Typography variant="caption">
                <strong>Post Anytime:</strong> Revfer can now create and publish content to the {totalAccounts} account{totalAccounts !== 1 ? 's' : ''} shown above
              </Typography>
            </li>
            <li>
              <Typography variant="caption">
                <strong>Your Approval Required:</strong> You'll review and approve all posts before they're published
              </Typography>
            </li>
            <li>
              <Typography variant="caption">
                <strong>Full Control:</strong> Manage connected accounts anytime from your Revfer dashboard
              </Typography>
            </li>
            <li>
              <Typography variant="caption">
                <strong>Revoke Anytime:</strong> Remove access from Facebook Settings → Business Integrations → Revfer
              </Typography>
            </li>
          </Box>
        </Alert>

        {/* CONNECTION TIMESTAMP */}
        <Paper 
          elevation={0}
          sx={{ 
            p: 1.5, 
            bgcolor: 'grey.50',
            mb: 3
          }}
        >
          <Typography variant="caption" color="text.secondary">
            <strong>Connected:</strong> {new Date().toLocaleString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </Typography>
        </Paper>

        {/* ACTION BUTTONS */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Button
            variant="outlined"
            onClick={onViewAccounts}
            startIcon={<ManageAccountsIcon />}
            fullWidth
            size="large"
          >
            Manage Accounts
          </Button>
          <Button
            variant="contained"
            onClick={onContinue}
            endIcon={<ArrowIcon />}
            fullWidth
            size="large"
          >
            Start Creating Content
          </Button>
        </Stack>

        {/* FOOTER NOTE */}
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ display: 'block', textAlign: 'center', mt: 3 }}
        >
          Keep this confirmation for your records. All connections are secure and can be managed from your dashboard.
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default OAuthSuccessConfirmation;